/*
 * Email Promo: Free Bottle Opener
 * -----------------------------------------------------------------------------
 * Activated by ?opener_promo=1 on any storefront URL (set by the promo email
 * link). While active for the browser session, this script keeps a free bottle
 * opener in sync with the cart:
 *
 *   . wine in cart + no opener + not manually removed  -> add opener
 *   . no wine in cart + opener present                 -> remove opener (auto)
 *   . customer removes the opener while wine remains    -> mark "manually removed",
 *                                                          never auto-add again
 *                                                          this session
 *
 * Cart-change detection is theme-agnostic: it intercepts the storefront's
 * /cart/* Ajax calls (fetch + XHR), which Dawn and every modern theme use.
 * Only refreshCartUI() is theme-aware - see the note on that function.
 */
(function () {
  'use strict';

  var cfg = window.EmailPromoOpenerConfig || {};
  var TAG = '[EmailPromoOpener]';
  var SS_ACTIVE = 'emailPromoOpener_active';
  var SS_MANUAL = 'emailPromoOpener_manuallyRemoved';

  function log()  { if (cfg.debug) console.log.apply(console, [TAG].concat([].slice.call(arguments))); }
  function warn() { console.warn.apply(console, [TAG].concat([].slice.call(arguments))); }

  // ?? session flags ??????????????????????????????????????????????????????????
  function ssGet(k)      { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v)   { try { sessionStorage.setItem(k, v); } catch (e) {} }
  function ssDel(k)      { try { sessionStorage.removeItem(k); } catch (e) {} }
  function isActive()        { return ssGet(SS_ACTIVE) === '1'; }
  function manuallyRemoved() { return ssGet(SS_MANUAL) === '1'; }

  // Activate (and reset a prior manual-removal) when arriving from the email link.
  try {
    var params = new URLSearchParams(window.location.search);
    if (params.get(cfg.promoParam || 'opener_promo') === '1') {
      ssSet(SS_ACTIVE, '1');
      ssDel(SS_MANUAL);
      log('Promo activated via URL');
    }
  } catch (e) { warn('Could not read URL params', e); }

  var OPENER_ID = Number(cfg.bottleOpenerVariantId);
  if (!OPENER_ID) {
    if (isActive()) warn('bottleOpenerVariantId not configured - promo cannot run. Set it in email-promo-opener.liquid.');
    return;
  }
  if (!isActive()) return; // nothing to do this session

  // ?? helpers ??????????????????????????????????????????????????????????????
  function variantIdOf(item) { return Number(item.variant_id || item.id); }
  function safeText(res)     { return res.text ? res.text().catch(function () { return ''; }) : Promise.resolve(''); }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  // Per-session cache of product type/tags (cart.js omits tags, so we fetch /products/<handle>.js once each).
  var productCache = {};
  function getProductMeta(handle) {
    if (!handle) return Promise.resolve({ type: '', tags: [] });
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('status ' + r.status); return r.json(); })
      .then(function (p) {
        var meta = {
          type: String(p.type || '').toLowerCase(),
          tags: (p.tags || []).map(function (t) { return String(t).toLowerCase(); })
        };
        productCache[handle] = meta;
        return meta;
      })
      .catch(function (e) {
        log('product meta fetch failed for', handle, e);
        var m = { type: '', tags: [] };
        productCache[handle] = m;
        return m;
      });
  }

  function isExcludedType(typeStr) {
    var t = String(typeStr || '').toLowerCase();
    if (!t) return false;
    return (cfg.excludeTypes || []).some(function (x) { return t.indexOf(String(x).toLowerCase()) !== -1; });
  }
  function typeIsWine(typeStr) {
    var t = String(typeStr || '').toLowerCase();
    if (!t || isExcludedType(t)) return false;
    return (cfg.type || ['wine']).some(function (x) { return t.indexOf(String(x).toLowerCase()) !== -1; });
  }
  function tagsAreWine(tags) {
    var list = (cfg.tags || []).map(function (x) { return String(x).toLowerCase(); });
    if (!list.length) return false;
    return (tags || []).some(function (tag) {
      return list.some(function (w) { return tag.indexOf(w) !== -1; });
    });
  }

  // Decide whether a cart line is a qualifying wine. Fast path on cart.js
  // product_type; falls back to the product's tags (needed for blank-type wines).
  function lineIsWine(item) {
    if (variantIdOf(item) === OPENER_ID) return Promise.resolve(false);
    var type = item.product_type || '';
    if (isExcludedType(type)) return Promise.resolve(false);
    if (typeIsWine(type)) return Promise.resolve(true);
    return getProductMeta(item.handle).then(function (meta) {
      if (isExcludedType(meta.type)) return false;
      return typeIsWine(meta.type) || tagsAreWine(meta.tags);
    });
  }

  // Is this line a "case" product (a multi-bottle case/box)? These carry no wine
  // tags/type, so we match by title/handle - e.g. "6 bottle box", "mixed case".
  function isCaseProduct(item) {
    var hay = String((item.product_title || item.title || '') + ' ' + (item.handle || '')).toLowerCase();
    return (cfg.caseMatch || ['case', 'box']).some(function (m) { return hay.indexOf(String(m).toLowerCase()) !== -1; });
  }

  // How many bottles a case holds, read from its name (e.g. "6 bottle box" -> 6,
  // "4-bottle-box" -> 4). Returns 0 if it isn't a case product or the count is unknown.
  function caseBottleCount(item) {
    if (!isCaseProduct(item)) return 0;
    var hay = String((item.product_title || item.title || '') + ' ' + (item.handle || '')).toLowerCase();
    var m = hay.match(/(\d+)\s*[- ]?bottle/);
    return m ? parseInt(m[1], 10) : 0;
  }

  // Cart qualifies for the free opener if it contains a case holding a full case
  // (>= minWineQuantity bottles), OR the total quantity of loose wine bottles
  // reaches minWineQuantity. Cases smaller than minWineQuantity (e.g. the 4-bottle
  // box) do NOT qualify and are excluded from the loose-bottle tally.
  function cartQualifies(items) {
    var min = Number(cfg.minWineQuantity) || 6;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (variantIdOf(it) === OPENER_ID || isExcludedType(it.product_type)) continue;
      if (caseBottleCount(it) >= min) return Promise.resolve(true);
    }
    return items.reduce(function (acc, it) {
      return acc.then(function (sum) {
        if (variantIdOf(it) === OPENER_ID || isCaseProduct(it)) return sum;
        return lineIsWine(it).then(function (w) { return w ? sum + (Number(it.quantity) || 0) : sum; });
      });
    }, Promise.resolve(0)).then(function (sum) { return sum >= min; });
  }

  // ?? cart mutations ?????????????????????????????????????????????????????????
  function addOpener() {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: OPENER_ID, quantity: 1, properties: { _email_promo: 'bottle-opener' } }] })
    }).then(function (r) {
      if (!r.ok) return safeText(r).then(function (t) { warn('add opener failed', t); return false; });
      log('Opener added');
      return true;
    });
  }

  function removeOpener() {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: String(OPENER_ID), quantity: 0 })
    }).then(function (r) {
      if (!r.ok) return safeText(r).then(function (t) { warn('remove opener failed', t); return false; });
      log('Opener removed (auto - no wine in cart)');
      return true;
    });
  }

  // ?? core sync loop ???????????????????????????????????????????????????????
  var lastOpenerPresent = false;
  var evalLock = false;
  var pendingEval = false;
  var suppressManual = false; // true when WE are removing the opener, so it isn't read as a customer removal

  function evaluate() {
    if (!isActive()) return;
    if (evalLock) { pendingEval = true; return; }
    evalLock = true;

    getCart().then(function (cart) {
      var items = cart.items || [];
      var openerPresent = items.some(function (i) { return variantIdOf(i) === OPENER_ID; });

      return cartQualifies(items).then(function (qualified) {
        // Customer removed the opener themselves (it vanished, cart still qualifies, not our doing).
        if (lastOpenerPresent && !openerPresent && qualified && !suppressManual) {
          ssSet(SS_MANUAL, '1');
          log('Customer removed the opener - it will not be auto-added again this session');
        }
        suppressManual = false;

        if (qualified && !openerPresent && !manuallyRemoved()) {
          return addOpener().then(function (ok) { if (ok) { lastOpenerPresent = true; refreshCartUI(); } });
        }
        if (!qualified && openerPresent) {
          suppressManual = true; // our removal, not the customer's
          return removeOpener().then(function (ok) {
            if (ok) { lastOpenerPresent = false; refreshCartUI(); }
            else { suppressManual = false; }
          });
        }
        lastOpenerPresent = openerPresent;
      });
    }).catch(function (e) {
      warn('evaluate error', e);
    }).then(function () {
      evalLock = false;
      if (pendingEval) { pendingEval = false; setTimeout(evaluate, 0); }
    });
  }

  // Debounced trigger so a burst of cart calls collapses into one evaluation.
  var debounceTimer = null;
  function scheduleEval() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(evaluate, 150);
  }

  // ?? cart-change detection (theme-agnostic) ??????????????????????????????????
  var CART_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  (function hookFetch() {
    if (!window.fetch || window.fetch.__epoHooked) return;
    var orig = window.fetch;
    window.fetch = function (input) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var p = orig.apply(this, arguments);
      if (CART_RE.test(url)) { p.then(function () { scheduleEval(); }).catch(function () {}); }
      return p;
    };
    window.fetch.__epoHooked = true;
  })();

  (function hookXHR() {
    var open = XMLHttpRequest.prototype.open;
    if (open.__epoHooked) return;
    var send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) { this.__epoUrl = url; return open.apply(this, arguments); };
    XMLHttpRequest.prototype.send = function () {
      if (CART_RE.test(this.__epoUrl || '')) {
        this.addEventListener('loadend', function () { scheduleEval(); });
      }
      return send.apply(this, arguments);
    };
    XMLHttpRequest.prototype.open.__epoHooked = true;
  })();

  // Some themes also fire their own events - harmless extra triggers.
  ['cart:updated', 'cart:change', 'cart:refresh', 'ajaxCart.afterCartLoad'].forEach(function (evt) {
    document.addEventListener(evt, scheduleEval);
  });

  // ?? cart UI refresh ?????????????????????????????????????????????????????????
  // NOTE: This is the only theme-specific part. The block below handles Dawn and
  // Shopify reference themes (cart-drawer / cart-icon-bubble via the Section
  // Rendering API) and dispatches generic events other themes listen to. If your
  // cart drawer doesn't visually refresh after an auto-add, adapt this function
  // to your theme's cart-refresh routine (see SETUP.md ? Troubleshooting).
  function refreshCartUI() {
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    } catch (e) {}

    var drawer = document.querySelector('cart-drawer');
    var bubble = document.getElementById('cart-icon-bubble');
    var cartItems = document.querySelector('cart-items, #main-cart-items');
    var sections = [];
    if (drawer) sections.push('cart-drawer');
    if (bubble) sections.push('cart-icon-bubble');
    if (cartItems) {
      var id = (cartItems.dataset && cartItems.dataset.id) || (cartItems.id) || 'main-cart-items';
      sections.push(id);
    }
    if (!sections.length) return; // not a Dawn-style theme - rely on events above

    fetch('/?sections=' + sections.join(','))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        function swap(node, sectionKey, selector) {
          if (!node || !data[sectionKey]) return;
          var doc = new DOMParser().parseFromString(data[sectionKey], 'text/html');
          var fresh = doc.querySelector(selector);
          if (fresh) node.innerHTML = fresh.innerHTML;
        }
        if (drawer) { swap(drawer, 'cart-drawer', 'cart-drawer'); drawer.classList.remove('is-empty'); }
        if (bubble) swap(bubble, 'cart-icon-bubble', '#cart-icon-bubble');
        if (cartItems) {
          var key = sections[sections.length - 1];
          swap(cartItems, key, 'cart-items, #main-cart-items');
        }
      })
      .catch(function (e) { log('Section refresh failed (likely a non-Dawn theme) - adapt refreshCartUI()', e); });
  }

  // ?? kick off ????????????????????????????????????????????????????????????????
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', evaluate);
  } else {
    evaluate();
  }
})();
